<script setup>
import Button from "~/components/atoms/Button.vue";
import Input from "~/components/atoms/Input.vue";
import Text from "~/components/atoms/Text.vue";
import Checkbox from "~/components/atoms/Checkbox.vue";
import ProductPrice from "~/components/molecules/ProductPrice.vue";
import KlarnaParts from "~/components/organisms/KlarnaParts.vue";
import DeliveryTimeCheck from "~/components/molecules/ProductPage/Checks/DeliveryTimeCheck.vue";
import ShowroomCheck from "~/components/molecules/ProductPage/Checks/ShowroomCheck.vue";
import UnitPerBoxCheck from "~/components/molecules/ProductPage/Checks/UnitPerBoxCheck.vue";
import NoticeAboutStCheck from "~/components/molecules/ProductPage/Checks/NoticeAboutStCheck.vue";
import Numbers from "~/components/molecules/ProductPage/Numbers.vue";
import {useCartStore} from "~/store/cartStore.js";
import {useQuotationStore} from "~/store/quotationStore.js";
import Help from "~/components/molecules/Help.vue";
import CartIcon from "~/components/icons/CartIcon.vue";
import WarehouseCheck from "~/components/molecules/ProductPage/Checks/WarehouseCheck.vue";
import BetterWith from "~/components/organisms/BetterWith.vue";
import Disclaimer from "~/components/molecules/Disclaimer.vue";

const props = defineProps(['unit', 'delivery_time_html', 'sample_is_available', 'image', 'name', 'sku', 'url', 'max_cart_qty', 'min_volume_to_buy', 'cart_multiplicity',
  'unit_per_box', 'min_volume_qty', 'sample_price', 'sample_delivery_time', 'status', 'id', 'supplementary', 'restparti',
  'pricePerBox', 'standard_price', 'volume_price', 'exists_in_showroom', 'another_size_in_stock_exists', 'niche_id', 'sample_modal_is_available', 'supplier_id', 'supplier_stock', 'product_type', 'optionalSupplementary', 'spillPercentage'])

const emit = defineEmits(['cartAdded', 'show-sample-form'])

const {t} = useI18n()
const device = useDevice()

const store = useCartStore()
const quotationStore = useQuotationStore()

const spillPercentageMultiplier = computed(() => 1 + (parseFloat(props.spillPercentage) || 10) / 100)
const count = ref(1)
const spill = ref(false)
const actions = ref(null)
const activeSticky = ref(false)
const hasMounted = ref(false)

const {
  unit, delivery_time_html, sample_is_available, sku, max_cart_qty,
  unit_per_box, min_volume_qty, sample_price, sample_delivery_time, status, id, restparti,
  exists_in_showroom, another_size_in_stock_exists, niche_id, supplier_stock, product_type, optionalSupplementary
} = toRefs(props)

const showShowroomCheck = computed(() => {
  return exists_in_showroom.value || another_size_in_stock_exists.value
})

const isSupplier196 = computed(() => Number(props.supplier_id) === 196)

const totalArea = computed(() => {
  if (props.unit === 'st') {
    return count.value
  }
  return Math.round(count.value * props.unit_per_box * 100) / 100
})

const volumeIsActive = computed(() => totalArea.value >= props.min_volume_qty)

const quantityUnit = computed(() => props.unit === 'm2' ? t('product.box') : (props.unit || 'st'))
const multiplicity = computed(() => Number(props.cart_multiplicity) || 1)
const minVolumeToBuy = computed(() => Number(props.min_volume_to_buy) || 1)
const showMultiplicityDisclaimer = computed(() => multiplicity.value > 1)
const showMinVolumeDisclaimer = computed(() => minVolumeToBuy.value > 1 && minVolumeToBuy.value !== multiplicity.value)
const minVolumeDisclaimerText = computed(() =>
    t('cart.min_volume_disclaimer', {
      count: minVolumeToBuy.value,
      unit: quantityUnit.value,
    })
)
const multiplicityDisclaimerText = computed(() =>
    t('cart.multiplicity_disclaimer', {
      count: multiplicity.value,
    })
)

const activePrice = computed(() => volumeIsActive.value ? props.volume_price : props.standard_price)

const totalPrice = computed(() => activePrice.value * count.value * (props.unit === 'st' ? 1 : props.unit_per_box))

function onScroll() {
  const elem = actions.value
  if (elem) {
    const top = elem.getBoundingClientRect().top
    if (top < -20) {
      activeSticky.value = true
    } else {
      activeSticky.value = false
    }
  }
}

function addSample() {
  if (props.sample_modal_is_available) {
    emit('show-sample-form')
  } else {
    store.addItemToCart({
      id: props.id,
      count: 1,
      sample: 1,
      source: store.sources.product_page_sample_button
    })
  }
}

function addProductToQuotation() {
  quotationStore.addProduct(props.image, props.name, props.sku, props.unit, props.unit_per_box, props.url, props.niche_id, props.status, props.product_type)
  quotationStore.openQuotation()
}

function minus() {
  count.value = parseInt(count.value)
  if (count.value > 1) {
    count.value--
  }
}

function plus() {
  const max = Number(props.max_cart_qty)
  count.value = parseInt(count.value)
  if (props.max_cart_qty !== null && props.max_cart_qty !== undefined && Number.isFinite(max) && max >= 1 && count.value >= max) {
    return
  }
  count.value++
}

watch(spill, (val) => {
  if (val) {
    count.value = Math.ceil(count.value * spillPercentageMultiplier.value)
  } else {
    count.value = Math.floor(count.value / spillPercentageMultiplier.value)
  }
})

watch(count, () => {
  const max = Number(props.max_cart_qty)
  if (props.max_cart_qty !== null && props.max_cart_qty !== undefined && Number.isFinite(max) && max >= 1 && count.value > max) {
    count.value = max
  }

  if (count.value < 1) {
    count.value = 1
  }
})

onMounted(() => {
  hasMounted.value = true

  count.value = 1

  window.addEventListener('scroll', onScroll)

  actions.value = document.querySelector('.actions')
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <div class="g-32">
    <div class="totals row full-width">
      <div class="numbers g-8 no-flex" style="padding-bottom: 8px;">
        <Numbers :unit="unit" :restparti="restparti"
                 :min_volume_qty="min_volume_qty"
                 :status="status"
                 :unit_per_box="unit_per_box" :volume-is-active="volumeIsActive" :activePrice="activePrice"
                 :totalArea="totalArea"/>
      </div>
      <div class="checks g-8" style="align-items: flex-end; max-width: 222px; margin-left: auto; text-align: right;">
        <ShowroomCheck
            v-if="!['Restparti','Lagerförsäljning','Offertvara'].includes(status) && showShowroomCheck"
            :another_size_in_stock_exists="another_size_in_stock_exists"
        />
        <UnitPerBoxCheck :unit_per_box="unit_per_box" :unit="unit"
                         v-if="!['Restparti'].includes(status) && unit === 'm2'"/>
        <DeliveryTimeCheck v-if="!supplier_stock || (supplier_stock && supplier_stock.warehouse_stock > 0)"
                           :unit="unit === 'm2' ? 'box' : unit"
                           :stock="supplier_stock ? supplier_stock.warehouse_stock : null"
                           :delivery_time_html="delivery_time_html"/>
        <WarehouseCheck v-if="supplier_stock" :unit="unit === 'm2' ? 'box' : unit"
                        :supplier_stock="supplier_stock.supplier_stock"
                        :available_date="supplier_stock.supplier_available_date"/>
        <NoticeAboutStCheck v-if="!isSupplier196 && niche_id === 'Kakel och Klinker' && unit === 'st'"/>
      </div>
    </div>

    <div class="g-16 the-same-gap">
      <div class="add-to-cart-block row full-width g-32"
           v-if="!isSupplier196 && !['Restparti','Offertvara'].includes(status) && !(['Lagerförsäljning', 'Discontinue'].includes(status) && max_cart_qty < 1 && max_cart_qty !== null)">
        <div class="row g-8 the-same-gap">
          <div class="row">
            <Button type="minus" @mousedown.prevent @click="minus"/>
            <Input width="32px" type="number" v-model="count"/>
            <Button type="plus" @mousedown.prevent @click="plus"/>
          </div>
          <Text class="unit" tag="strong">{{ unit === 'm2' ? 'box' : unit }}</Text>
        </div>
        <div class="row no-flex g-4">
          <template v-if="!isSupplier196 && (niche_id==='Kakel och Klinker' || niche_id==='Golv & Vägg')">
            <Checkbox v-model="spill" id="spill" :text="'+' + (spillPercentage || 10) + '% ' + $t('product.extra_box')"/>
            <Help v-if="niche_id==='Kakel och Klinker'">{{ $t('product.extra_box_text') }}</Help>
            <Help v-else>{{ $t('product.extra_box_text_golv_and_vagg') }}</Help>
          </template>
        </div>
        <div style="margin-left: auto;">
          <Text style="width: fit-content; margin-left: auto;" tag="strong">
            <ProductPrice class="reset total" :light-unit="true" :price="totalPrice"/>
          </Text>
        </div>
      </div>
      <Disclaimer
          v-if="showMultiplicityDisclaimer"
          class="min-volume-disclaimer"
          :text="multiplicityDisclaimerText"
      />
      <Disclaimer
          v-if="showMinVolumeDisclaimer"
          class="min-volume-disclaimer"
          :text="minVolumeDisclaimerText"
      />

      <section v-if="optionalSupplementary?.length" class="optional-supplementary" style="margin-bottom: 10px;">
        <BetterWith :vertical="true" ga-key="tillval_caclulator_product_page"
                    :title="t('product.optional').toUpperCase()" :sku="sku"
                    :items="optionalSupplementary"/>
      </section>

      <div class="actions full-width g-16 the-same-gap">
        <div
            v-if="!['Offertvara'].includes(status) && !(['Lagerförsäljning', 'Discontinue'].includes(status) && max_cart_qty < 1 && max_cart_qty !== null)">
          <Button class="full-width tall coarse uppercase"
                  @click="store.addItemToCart({id,count,source:store.sources.product_page_add_to_cart_button}); emit('cartAdded')"
                  type="black">
            <CartIcon width="18px" height="18px" stroke="white"/> &nbsp;
            {{ $t('cart.add_to_cart') }}
          </Button>
        </div>
        <div class="row full-width g-16 the-same-gap">
          <Button @click="addSample"
                  v-if="sample_is_available  &&
              sample_price !== null &&
              !['Offertvara','Discontinue'].includes(status) &&
              !(['Lagerförsäljning', 'Discontinue'].includes(status) && max_cart_qty < 1 && max_cart_qty !== null)"
                  :sub_text="sample_delivery_time"
                  class="full-width tall"
                  :help="hasMounted && device.isDesktop ? (product_type === 'Vinylgolv' ? $t('product.about_sample_vinyl') : $t('product.about_sample')) : null"
                  type="bronze">
            {{ $t('product.sample') }} -&nbsp;<ProductPrice :digits="0" :price='sample_price'/>
          </Button>
          <Button @click="addProductToQuotation" v-if="status!=='Restparti'" class="full-width tall"
                  :text="$t('quotation.company-quotation')"
                  type="bronze"/>
        </div>
      </div>

      <KlarnaParts v-if="!['Offertvara','Discontinue'].includes(status)" :total-price="totalPrice"/>
    </div>

    <div class="mobile-sticky-buy"
         v-if="hasMounted && device.isMobile && !(['Lagerförsäljning', 'Discontinue'].includes(status) && max_cart_qty !== null && max_cart_qty < 1)"
         :class="{active:activeSticky}">
      <div class="row full-width g-16 the-same-gap">
        <Button
            v-if="!['Offertvara'].includes(status) && !(['Lagerförsäljning', 'Discontinue'].includes(status) && max_cart_qty < 1 && max_cart_qty !== null)"
            class="full-width tall coarse uppercase"
            @click="store.addItemToCart({id,count,sample:0,source:store.sources.product_page_add_to_cart_fixed_button}); emit('cartAdded')"
            type="black">
          <CartIcon width="18px" height="18px" stroke="white"/> &nbsp;
          {{ $t('cart.add_to_cart') }}
        </Button>
        <Button @click="addSample"
                v-if="sample_is_available && sample_price !== null && !['Offertvara','Discontinue'].includes(status)"
                :sub_text="sample_delivery_time"
                class="full-width tall"
                :help="hasMounted && device.isDesktop ? (product_type === 'Vinylgolv' ? $t('product.about_sample_vinyl') : $t('product.about_sample')) : null"
                type="bronze">
          {{ $t('product.sample') }} -&nbsp;<ProductPrice :digits="0" :price='sample_price'/>
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

* {
  font-size: 12px;
}

.unit {
  font-size: 14px;
}

.totals {
  color: $light-black;
}

.total {
  font-size: 13px !important;
}

.mobile-sticky-buy {
  position: fixed;
  bottom: -74px;
  z-index: 999;
  left: 0;
  right: 0;
  background: white;
  padding: 12px 16px;
  transition: all .3s ease-in-out;

  &.active {
    bottom: 0;
  }
}

.numbers:deep(.volume-is-active) {
  width: 82px;
  height: 23px;
  display: inline-block;
  font-size: 13px;
  right: -150%;
  padding: 2px 5px;
  margin-left: 10px;
  position: absolute;
  z-index: 9;
  top: -5px;
}

.add-to-cart-block:deep(input) {
  font-size: 14px;
}

.min-volume-disclaimer {
  width: 100%;
}

.optional-supplementary {
  max-height: 400px;
  overflow: auto;

  @include smd {
    max-height: 300px;
  }
}
</style>
